import { ThisReceiver } from '@angular/compiler';
import { Injectable } from '@angular/core';
import { Utils } from '@mitel/cloudlink-sdk';
import { ResetUserPasswordRequest, User } from '@mitel/cloudlink-sdk/admin';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserRoles } from '../constants/constants';
import { UserData } from '../interfaces/user-data';
import { Util } from '../utils/Util';
import { AccountsService } from './accounts.service';
import { AdminService } from './admin.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { AuthService } from './auth.service';

@Injectable({
	providedIn: 'root'
})
export class UsersService {


	public usersChanged = new BehaviorSubject<User[]>([]);
	public usersUpdated = new Subject<User[]>();
	public loading = new Subject<boolean>();
	private _users: User[] = [];
	me: User;
	_partnerUsers: User[] = [];
	public partnerUsersChanged = new Subject<User[]>();
	subscription: Subscription = new Subscription();
	private partnerAdminObservable;

	private next ;

	private _multiEditUsersData: any = undefined;

	private getModByUserCacheObservable: { [userId: string]: Observable<string> } = {};
	private getModByUserCache: { [userId: string]: string } = {}
	modUserCachesubscription: Subscription = new Subscription();

	get users(): User[] {
		return this._users;
	}

	set users(value: User[]) {
		this._users = value;
		this.usersChanged.next([...this._users]);
	}

	get multiEditUsersData(): any {
		return this._multiEditUsersData;
	}

	set multiEditUsersData(value: any) {
		this._multiEditUsersData = value;
	}

	constructor(
		private adminService: AdminService,
		private authService: AuthService,
		private claimsService: AppUserClaimsService,
		private accountService: AccountsService,
		private translateService: TranslateService, 
	) { }

	async getUsers(accountId: string, params: any) {
			try {
				this.next = undefined;
				this.clearModUserCache(); // clear the mod by admin because it might be a new account
				const userParams = this.getUserParameters(params);
				let	users = await this.getUsersByAccountId(accountId, userParams);
				this.users = (users && users.length > 0) ? users : [];
			} catch (err) {
				console.log('Failed to fetch the users of account ', accountId);
				this.users = [];
			}
	}

	private getUserParameters(params: any) {
		const userParams = {};
		userParams['$Filter'] = "active ne 'false'";
		userParams['$Expand'] = "tags";
		if (params.expand) {
			userParams['$Expand'] += `,${params.expand}`;
		}
		if (params.filter) {
			if (params.filter.name === 'unverified') {
				// we need if userVerified is false or if userVerified doesn't exist
				userParams['$Filter'] += " and " + params.filter.property + " ne '" + params.filter.value + "'";
			} else if (params.filter.name === 'inactive') {
				userParams['$Filter'] = params.filter.property + " eq '" + params.filter.value + "'";
			} else if (params.filter.name !== 'admins') {
				userParams['$Filter'] += " and " + params.filter.property + " eq '" + params.filter.value + "'";
			} else {
				userParams['$Filter'] += " and role ne 'USER' and role ne 'GLOBAL_ADMIN' and role ne 'GUEST'";
			}
		}
		if (params.orderBy) {
			const order = params.orderBy.value ? 'asc' : 'desc';
			if (params.orderBy.property === 'displayName') {
				userParams['$Orderby'] = 'name ' + order + ',firstName ' + order + ',lastName ' + order;
			} else {
				userParams['$Orderby'] = params.orderBy.property + ' ' + order;
			}
		}
		if (params.search) {
			userParams['$SearchKeys'] = 'name, firstName, lastName, email, extension';
			userParams['$Search'] = params.search;
			if (params.searchKeys) {
				userParams['$SearchKeys'] = params.searchKeys;
			}
		}
		return userParams;
	}

	async searchUserByFieldFilter( accountId: string, fieldFilter: string, searchValue: string ): Promise<User>
	{
		const userParams = {};
		let searchStr = fieldFilter + " eq '" + searchValue + "'";
		userParams['$Filter'] = encodeURIComponent(searchStr);

		try {
			let	users = await this.adminService.getUsersByAccountId(accountId, userParams);
			// since this is a very targeted search of a parameter that most 
			// likely can not be duplicated there should only be one user
			// if in the future this method is used for more than it currently is
			// we will need to revisit how this is used
			return (users && users.length > 0) ? users[0] : undefined;
		} catch (err) {
			console.warn('Failed to fetch the users of account ', accountId);
			return undefined;
		}
	}

	async getUserById(accountId: string, userId: string): Promise<User> {
		let elIndex = this.users.findIndex((user) => user.userId === userId);
		if (elIndex !== -1) {
			console.log('User Found in the list');
			return Promise.resolve(this.users[elIndex]);
		} else {
			try {
				console.log('Did not Found User in the list');
				let user = await this.adminService.getUserFromAccount(accountId, userId);
				if (this.users.length > 0) {
					this.users.push(user);
				}	
				return Promise.resolve(user);
			} catch (err) {
				console.log('UserService: getUserById ', err);
				return Promise.reject(err);
			}
		}
	}

	// serves a dual purpose for users and entities
	extractDisplayName( user: any ): string
	{
		let name;
		if ( user.entityType && user.entityType == "user" )
			name = user.name;
		else if ( user.entityType && user.entityType == "client" )
			name = "SOLUTION_CLIENT"; // we will not make the string here and let the component do it
		else if ( user.entityType )
			name = user.name ? user.name : user.entityId;
		else // not an entity, a cached user instead
		{
			name = user.displayName ? user.displayName : 
			(user.lastName ? user.lastName : 
				user.firstName ? user.firstName : user.userId);
		}
		return name;
	}

	// very similar to the getUser by ID but we do not want this user to be added to the 
	// users list as it is not always appropriate (filtering, partner admin)
	async getModByUserNameById( user: User ): Promise<string> {
		// this will save a micro call
		if ( user.userId == user.modifiedBy ) // the user themself was the last editor
		{			
			return Promise.resolve(this.extractDisplayName(user));
		}

		let elIndex = this.partnerUsers.findIndex((puser) => puser.userId === user.modifiedBy);
		if (elIndex !== -1) 
		{
			// we have a list of partner users in the partner login case, so lets see if the users is in that list
			// if we have already queried for this user so lets not do it again	
			return Promise.resolve(this.extractDisplayName(this.partnerUsers[elIndex]));
		}

		return this.getCachedModByUserName(user.accountId, user.modifiedBy);
	}

	// in a effort to not hammer the system, we will make one request per user and the rest will wait on that
	// request to return
	getCachedModByUserName( accountsId: string, userId: string ): Promise<string> 
	{
		return new Promise<string> ( (resolve, reject) => {
			if ( this.getModByUserCache[userId] != undefined )
			{
				return resolve(this.getModByUserCache[userId]);
			}
			else
			{
				// user is not in the cache, so request it
				if ( this.getModByUserCacheObservable[userId] == undefined )
				{
					this.getModByUserCacheObservable[userId] = this.getCachedModByUserNameObservable( accountsId, userId );
				}
				this.modUserCachesubscription.add(this.getModByUserCacheObservable[userId].subscribe(username => {
					this.getModByUserCache[userId] = username;
					return resolve(username);
				}));
			}
		});
	}

	getCachedModByUserNameObservable( accountId: string, userId: string ): Observable<string>
	{
		return Observable.create( async (observer) => {
			return this.adminService.getEntity( userId ).then(user => {
				let userName = this.extractDisplayName( user );
				return observer.next(userName);
			})
			.catch( err => {
				//we will log and return null
				console.error("There was an error requesting the user information", err);
				return observer.next(null);
			});
		})
		.pipe(
			tap(() => { console.debug('getEntity method executed')}),
			shareReplay()
		);
	}

	clearModUserCache()
	{
		this.modUserCachesubscription.unsubscribe();
		this.modUserCachesubscription = new Subscription();
		this.getModByUserCacheObservable = {};
		this.getModByUserCache = {};
	}

	async getUsersOnScrolling(accountId: string) {
		if (this.users && this.users.length > 0 && this.users[0].accountId === accountId && this.next) {
			try{
				let nextUsers = await this.getUsersByAccountId(accountId, this.next);
				console.log(" getUsersOnScrolling users", nextUsers);
				if (nextUsers && nextUsers.length > 0) {
					this.users.push(...nextUsers);
				}
				this.usersUpdated.next([...nextUsers]);
			} catch (err) {
				console.log('Failed to fetch the users on scrolling of account ', accountId);
			}
		}
		else
		{
			// there is no more
			this.usersUpdated.next([]);
		}
		console.log("done - getUsersOnScrolling", this.users)
	}

	isNextUserBatchPresent(): boolean {
		return this.next ? true : false ;
	}

	// beware, this method only returns the first 50 users
	// if you want all users, go to the adminservice and use the method by the same name
	async getUsersByAccountId(accountId: string, params: any): Promise<User[]> {
		return new Promise<User[]>(async (resolve, reject) => {
			let users: User[];
			try {
				const userCollection = await this.adminService.getUsersFromAccount(accountId, params);
				if (userCollection) {
					users = Utils.getItemsFromCollection(userCollection);
					if ( userCollection._links && userCollection._links.next) {
						this.next = Utils.getOdataNext(userCollection);
						this.next['$SearchKeys'] = Util.getParameterFromNextLink('searchKeys', userCollection._links.next); // Key has to be $SearchKeys while in next link it will be $searchKeys
						console.log(this.next);
					} else {
						this.next = undefined;
					}
					
				}
				resolve(users);
			} catch (err) {
				this.next = undefined;
				reject(err);
			}
		});	
	}
	// only creates a user
	createUserForAccount(accountId: string, params: any): Promise<User> {
		return this.adminService.createUserForAccountWithoutSpinner(accountId, params);
	}

	createOrUpdateNewUser(accountId: string, params: any, skipEmail?: boolean): Promise<User> {
		return new Promise<User>(async (resolve, reject) => {
			try {
				let result;
				if (params.id) {
					result = await this.adminService.updateUserByAccountIdWithoutSpinnerAndNotify(accountId, params.id, params);     
				} else {
					result = await this.adminService.createUserForAccount(accountId, params);
					if(!skipEmail){
						this.sendWelcomeEmail(result)
						.catch(err => console.warn('Function: , error on sendUserWelcome: ', err));
					}
				}
				resolve(result);
			} catch (err) {
				console.warn('could not get user info', err);
				if (!err) {
					reject('Could not get user info');
				} else if (err.body) {
					reject(err.body);
				} else {
					reject(err);
				}
			}
		});
	}

	sendWelcomeEmail(user: User): Promise<any> {
		return this.adminService.sendWelcomeEmail(this.accountService.getCurrentAccount().accountId, user.userId);
	}

	updateUserPhoto(userId: string, file: any): Promise<any> {
		return this.adminService.updateUserPhoto(userId, file.type, file);
	}

	resetPassword(user: User): Promise<any> {
		return new Promise(async (resolve, reject) => {
			try {
				let body: ResetUserPasswordRequest = {
					action: 'forgot',
					accountId: this.accountService.getCurrentAccount().accountId,
					type: 'email',
					clientId: environment.clientId
				};
				await this.adminService.resetUserPassword(user.userId, body);
				resolve(true);
			} catch (err) {
				reject(err);
			}
		});
	}


	deleteUser(accountId: string, user: UserData): Promise<any> {
		return new Promise(((resolve, reject) => {
			let claim = this.claimsService.getClaims();
			// @ts-ignore
			if (claim.uniqueUserId !== user.id) {
				return this.adminService.deleteUserByAccountId(accountId, user).then((result) => {
					this.removeUserFromModel(result);
					if (this.next && this.next.$Skip) {
						this.next.$Skip -= 1;
					}
					resolve(result);
				}).catch(err => reject(err));
			} else {
				reject('Attempt to delete yourself');
			}
		}));
	}

	private removeUserFromModel(uniqId: string) {
		this.users = this.users.filter((user) => user.uniqueUserId !== uniqId);
	}

	getMe(): Observable<User | {error: any}> {
		this.loading.next(true);
		return Observable.create( async (observer) => {
			if (this.me) {
				this.loading.next(false);
				observer.next(this.me);
			}
			try {
				let claim = this.claimsService.getClaims();
				let options = undefined;
				if ( this.claimsService.isAssumedRole () )
				{
					options = this.authService.getPartnerAuthHeader();
				}

				// @ts-ignore
				this.me = await this.adminService.getUserWithoutFrills(claim.uniqueUserId, 'tags', options);
				this.loading.next(false);
				observer.next(this.me);
			} catch (e) {
				this.loading.next(false);
				observer.next({error: e});
			}
		});

	}

	getPartnerAdminObservable() {
		return Observable.create( async (observer) => {
				const claims = this.claimsService.getClaims();
				if ( (claims && claims.role === 'PARTNER_ADMIN') || this.claimsService.isAssumedRole()) {
					try {
						let options;
						if ( this.claimsService.isAssumedRole() ) {
							options = this.authService.getPartnerAuthHeader();
						}
						const partnerUsers = await this.adminService.getUsersByAccountId(claims.accountId, {$Filter: "role eq 'PARTNER_ADMIN'"}, options);
						this.partnerUsers = partnerUsers;
						observer.next(partnerUsers);
					} catch (err) {
						observer.next({error: err});
					}
					
				} else {
					this.partnerUsers = [];
					observer.next([]);
				}	
		}).
		pipe(
			tap(() => { console.log('getPartnerAdmins() method executed')}),
			shareReplay()
		);
	}

	get partnerUsers() {
		return this._partnerUsers;
	}

	set partnerUsers(partnerAdmins: User[]) {
		this._partnerUsers = partnerAdmins;
		this.partnerUsersChanged.next([...this._partnerUsers]);
	}

	getAllPartnerUsers( tries?: number) {
		if ( tries === undefined ||  tries > 0) {
			this.partnerAdminObservable = undefined;
			this.partnerAdminObservable = this.getPartnerAdminObservable();
			const claims = this.claimsService.getClaims();
			if ( (claims && claims.role === 'PARTNER_ADMIN') || this.claimsService.isAssumedRole()) {
				this.subscription.add(this.partnerAdminObservable.subscribe((partnerAdmins) => {
					if (Array.isArray(partnerAdmins)) {
						console.log('Fetched partner Users successfully: ', partnerAdmins);
					} else {
						console.log('Failed to fetch partner users', partnerAdmins.error);
						tries === undefined ? this.getAllPartnerUsers(2) : this.getAllPartnerUsers(--tries);
					}
				}));
			}
		} 
	}
	
	getPartnerAdmins(): Observable<User[] | {error: any}> {
		return this.partnerAdminObservable;
	}

	destroySubscriptions() {
		if( this.subscription) {
			this.subscription.unsubscribe();
		}
		this.clearModUserCache();
	}

	async getModifiedByName(user: User): Promise<string> {
    if (!user.modifiedBy)
      return Promise.resolve(this.translateService.instant('users_list.unknownUser'));

    if (user.modifiedBy == "platform-api-billing" || user.modifiedBy == "mivb-provisioning")
      return Promise.resolve(this.translateService.instant('users_list.system'));

    if (user.modifiedBy.toLocaleLowerCase() == "scim")
      return Promise.resolve("SCIM"); // no need to translate
    try {
      // ok lets resolve that user id
      let usersName = await this.getModByUserNameById(user);

      if (usersName && usersName == "SOLUTION_CLIENT")
        // did not want the service to have to pull in the translations
        return Promise.resolve(this.translateService.instant('users_list.solution'));
      else if (usersName)
        return usersName;

      // no users name so return unknown
      return this.translateService.instant('users_list.unknownUser');
    }
    catch (err) {
      // should not happen
      return this.translateService.instant('users_list.unknownUser');
    }
  }
}
